import { useEffect, useRef, useState } from 'react';

import { Box, Flex, Skeleton } from '@chakra-ui/react';
import { StyledPaper, StyledTypography } from 'styles/common';

import { Plus, Delete } from 'assets/assetsV1/iconComponents';

import { Button } from 'uiToolkitV1/Button';
import { InputV2 } from 'uiToolkitV1/InputV2';
import { PaperWithShadow } from 'uiToolkitV1/Paper';
import { TextBodyMedium, TextHeader3 } from 'uiToolkitV1/Typography';

const NotesList = ({ notesList, setNotes }) => {
  const handleDeleteNote = (targetNote) => {
    const updatedNotes = notesList.filter((note) => note !== targetNote);
    setNotes(updatedNotes);
  };

  return notesList.map((note) => (
    <Flex
      flexDirection="row"
      justifyContent="space-between"
      py="12px"
      borderBottom="1px solid #E7E7E7"
      alignItems="center"
    >
      <Box width="320px" overflow="auto" borderBottom="1px dashed #999" py="8px">
        <TextBodyMedium>{note}</TextBodyMedium>
      </Box>
      <Box onClick={() => handleDeleteNote(note)} cursor="pointer">
        <Delete boxSize="20px" />
      </Box>
    </Flex>
  ));
};

const AddButtonSection = ({ isAddMode, setIsAddMode, notes, setNotes }) => {
  const [inputValue, setInputValue] = useState('');
  const ref = useRef(null);

  const handleAddNoteToList = () => {
    const isDuplicate = notes.find((note) => note === inputValue);
    if (!isDuplicate && inputValue) {
      setNotes([...notes, inputValue]);
    }
    setIsAddMode(false);
    setInputValue('');
  };

  const handleCheckEnter = (event) => {
    if (event.key === 'Enter') {
      handleAddNoteToList();
    }
  };

  useEffect(() => {
    if (isAddMode && ref.current) {
      ref.current.focus();
    }
  }, [isAddMode]);

  return (
    <Box>
      {isAddMode && (
        <InputV2
          ref={ref}
          type="text"
          isDashed
          value={inputValue}
          onChange={(event) => setInputValue(event?.currentTarget?.value)}
          width="320px"
          onBlur={handleAddNoteToList}
          onKeyDown={(e) => handleCheckEnter(e)}
        />
      )}
      <Button variant="tertiary" StartIcon={Plus} onClick={() => setIsAddMode(true)}>
        Add new type
      </Button>
    </Box>
  );
};

const NotesSection = ({ notesList, setNotesList, isLoading, isError }) => {
  const [isAddMode, setIsAddMode] = useState(false);

  if (isLoading) {
    return (
      <Box display="flex" mb={3}>
        <Skeleton height="100px" width="100%" borderRadius="8px" startColor="surface.F5F5F5" endColor="surface.g3" />
      </Box>
    );
  }

  if (isError) {
    return (
      <StyledPaper padding="1rem" elevation={0} showBorder bottomGutter={1}>
        <StyledTypography variant="body2" color="red" align="center">
          Error occurred while fetching disciplinary violations
        </StyledTypography>
      </StyledPaper>
    );
  }

  return (
    <PaperWithShadow p={3} mb={3}>
      <Box borderBottom="1px solid #E7E7E7" py="4px">
        <TextHeader3 bold>Configure types</TextHeader3>
      </Box>
      <Box maxH="260px" overflow="auto">
        <NotesList notesList={notesList} setNotes={setNotesList} />
      </Box>

      <AddButtonSection isAddMode={isAddMode} setIsAddMode={setIsAddMode} notes={notesList} setNotes={setNotesList} />
    </PaperWithShadow>
  );
};

export default NotesSection;
